import React, { useState, useMemo } from 'react';
import {
    FiChevronDown,
    FiChevronUp,
    FiEdit2,
    FiTrash2,
    FiCheckCircle,
    FiAlertCircle,
    FiFileText,
} from 'react-icons/fi';

export const SALES_BILL_TABLE_COL_COUNT = 15;

export const salesBillTableColGroup = (
    <colgroup>
        <col className="w-10" />
        <col className="w-14" />
        <col className="min-w-[100px]" />
        <col className="min-w-[90px]" />
        <col className="min-w-[160px]" />
        <col className="min-w-[160px]" />
        <col className="min-w-[140px]" />
        <col className="min-w-[120px]" />
        <col className="min-w-[90px]" />
        <col className="min-w-[90px]" />
        <col className="min-w-[90px]" />
        <col className="min-w-[100px]" />
        <col className="min-w-[90px]" />
        <col className="min-w-[100px]" />
        <col className="min-w-[90px]" />
    </colgroup>
);

export function useSalesReportTableSort(paginatedData) {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
        }));
    };

    const sortedPaginatedData = useMemo(() => {
        if (!sortConfig.key) return paginatedData;
        const sorted = [...paginatedData];
        sorted.sort((a, b) => {
            const key = sortConfig.key;
            let aVal = a[key];
            let bVal = b[key];

            if (key === 'GrandTotal') {
                aVal = parseFloat(a.TotalAmount || 0) + parseFloat(a.DiscountAmount || 0);
                bVal = parseFloat(b.TotalAmount || 0) + parseFloat(b.DiscountAmount || 0);
            } else if (['TaxAmount', 'DiscountAmount', 'TotalAmount', 'BalanceDue', 'InvoiceNo'].includes(key)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else if (key === 'CreatedDate' || key === 'EntryDate') {
                aVal = new Date(a.CreatedDate || a.EntryDate || 0).getTime();
                bVal = new Date(b.CreatedDate || b.EntryDate || 0).getTime();
            } else {
                aVal = String(aVal ?? '').toLowerCase();
                bVal = String(bVal ?? '').toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [paginatedData, sortConfig]);

    const stickyThClass = 'sticky top-0 z-20 bg-gray-200';

    const SortableHeader = ({ sortKey, label, className = 'text-left' }) => (
        <th
            className={`px-4 py-3 ${className} text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap cursor-pointer hover:bg-slate-300/60 transition-colors ${stickyThClass}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className={`flex items-center ${className.includes('right') ? 'justify-end' : ''}`}>
                {label}
                {sortConfig.key === sortKey && (
                    <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                )}
            </div>
        </th>
    );

    return { sortedPaginatedData, SortableHeader, stickyThClass };
}

export function InvoiceItemsDetailTable({ details, invoiceKey, loadingDetails, userInfo }) {
    if (loadingDetails?.has?.(invoiceKey)) {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mb-3"></div>
                <p className="text-sm text-slate-500">Loading invoice details...</p>
            </div>
        );
    }

    if (!details?.details?.length) {
        return (
            <div className="text-center py-6">
                <p className="text-sm text-slate-500">No invoice details available</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-max w-full divide-y divide-slate-200 text-xs text-slate-700">
                <thead className="bg-slate-200 sticky top-0 z-10">
                    <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">SI</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Item Name</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Batch</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">MRP</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Sales Price</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Sales Rate</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Total</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Disc %</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Disc Amt</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Tax</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Tax Code</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Net Total</th>
                        {userInfo?.Designation === 'Admin' && (
                            <th className="px-3 py-2 text-right text-xs font-semibold text-green-700 bg-green-50 whitespace-nowrap">Profit</th>
                        )}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                    {details.details.map((detail, detailIndex) => (
                        <tr key={detailIndex} className="hover:bg-indigo-50/30">
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{detailIndex + 1}</td>
                            <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{detail.ItemName || '-'}</td>
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{detail.BatchCode || '-'}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{parseFloat(detail.Qty || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.MRP || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.SalesPrice || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">
                                {(() => {
                                    const mrp = parseFloat(detail.MRP || 0);
                                    const taxCode = parseFloat(detail.TaxCode || 0);
                                    const salesRate = mrp - (mrp * (taxCode / 100));
                                    return `₹${salesRate.toFixed(2)}`;
                                })()}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.Total || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{parseFloat(detail.Disc_Percentage || 0).toFixed(2)}%</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.Disc_Amount || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.TaxAmount || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{detail.TaxCode || '-'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">
                                ₹{(parseFloat(detail.NetTotal || 0) + parseFloat(detail.TaxAmount || 0)).toFixed(2)}
                            </td>
                            {userInfo?.Designation === 'Admin' && (
                                <td className="px-3 py-2 text-right font-semibold text-green-700 bg-green-50/50 whitespace-nowrap">
                                    ₹{parseFloat(detail.GP_Amount || 0).toFixed(2)}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {userInfo?.Designation === 'Admin' && details.header && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
                    <span className="text-green-700 font-medium">Total Profit %: </span>
                    <span className="font-bold text-green-800 mr-4">{parseFloat(details.header.GP_Percentage || 0).toFixed(2)}%</span>
                    <span className="text-green-700 font-medium">Total Profit: </span>
                    <span className="font-bold text-green-800">₹{parseFloat(details.header.GPAmount || 0).toFixed(2)}</span>
                </div>
            )}
        </div>
    );
}

const PaymentStatusCell = ({ balanceDue, isIpCredit }) => {
    if (isIpCredit) {
        return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200">
                IP credit
            </span>
        );
    }
    if (balanceDue > 0) {
        return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                <FiAlertCircle size={14} className="mr-1" />
                Pending
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FiCheckCircle size={14} className="mr-1" />
            Paid
        </span>
    );
};

export default function SalesReportBillsTable({
    rows,
    startIndex = 0,
    expandedBills,
    invoiceDetails,
    loadingDetails,
    onToggleExpand,
    formatInvoice,
    userInfo,
    showUserName = true,
    showActions = false,
    onEdit,
    onDelete,
    showIpType = false,
    isIpCreditBill,
    showBalanceDue = true,
}) {
    const { sortedPaginatedData, SortableHeader, stickyThClass } = useSalesReportTableSort(rows);
    
    let colCount = 6; // expand, SI, Inv, Date, Patient, Doctor
    if (showIpType) colCount += 1;
    if (showUserName) colCount += 1;
    colCount += 4; // G Total, Tax, Discount, Total Amount
    if (showBalanceDue) colCount += 1;
    colCount += 1; // Payment
    if (showActions) colCount += 1;

    if (!rows?.length) {
        return (
            <div className="text-center py-12">
                <FiFileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No data available for the selected period</p>
            </div>
        );
    }

    return (
        <div className="mt-2 flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-max divide-y divide-slate-200 text-sm text-slate-700 border-collapse">
                {salesBillTableColGroup}
                <thead className="bg-gray-200">
                    <tr>
                        <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}></th>
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                            SI No
                        </th>
                        <SortableHeader sortKey="InvoiceNo" label="Inv. No" />
                        {showIpType && (
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                                Type
                            </th>
                        )}
                        <SortableHeader sortKey="CreatedDate" label="Date" />
                        <SortableHeader sortKey="PatientName" label="Patient Name" />
                        <SortableHeader sortKey="DoctorName" label="Doctor Name" />
                        {showUserName && <SortableHeader sortKey="UserName" label="User Name" />}
                        <SortableHeader sortKey="SoldQty" label="Sold Qty" className="text-right" />
                        <SortableHeader sortKey="TotalAmount" label="Total Amount" className="text-right" />
                        {showBalanceDue && (
                            <SortableHeader sortKey="BalanceDue" label="Balance Due" className="text-right" />
                        )}
                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                            Payment
                        </th>
                        {showActions && (
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                                Actions
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                    {sortedPaginatedData.map((row, index) => {
                        const invoiceKey = `${row.Sequence}_${row.InvoiceNo}`;
                        const details = invoiceDetails?.[invoiceKey];
                        const totalAmount = parseFloat(row.TotalAmount || 0);
                        const discountAmount = parseFloat(row.DiscountAmount || 0);
                        const grandTotal = totalAmount + discountAmount;
                        const balanceDue = parseFloat(row.BalanceDue || 0);
                        const isCredit = showIpType && isIpCreditBill?.(row);
                        const mobileNo = details?.header?.MobileNo
                            ? String(details.header.MobileNo).trim()
                            : null;
                        const isExpanded = expandedBills?.has?.(invoiceKey);
                        const siNo = startIndex + index + 1;

                        return (
                            <React.Fragment key={`${row.InvoiceNo}_${row.Sequence}_${index}`}>
                                <tr className="border-b border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                                    <td className="px-3 py-3 whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => onToggleExpand?.(row)}
                                            className="inline-flex items-center justify-center p-1 text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                                            title={isExpanded ? 'Hide line items' : 'Show line items'}
                                        >
                                            {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-700 font-medium">{siNo}</td>
                                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-indigo-700">
                                        {formatInvoice ? formatInvoice(row) : row.InvoiceNo}
                                    </td>
                                    {showIpType && (
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                                    isCredit
                                                        ? 'bg-amber-100 text-amber-900 border border-amber-200'
                                                        : 'bg-slate-100 text-slate-800 border border-slate-200'
                                                }`}
                                            >
                                                {isCredit ? 'Credit' : 'Debit'}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                                        {row.CreatedDate
                                            ? `${new Date(row.CreatedDate).toLocaleDateString('en-GB')} ${new Date(row.CreatedDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                            : row.EntryDate
                                                ? new Date(row.EntryDate).toLocaleDateString('en-GB')
                                                : '-'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-700 min-w-[140px]">
                                        <div className="font-medium">{row.PatientName || 'N/A'}</div>
                                        {mobileNo && <div className="text-xs text-gray-500">{mobileNo}</div>}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-slate-700 min-w-[120px]">
                                        {row.DoctorName || '-'}
                                    </td>
                                    {showUserName && (
                                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                                            {row.UserName || 'N/A'}
                                        </td>
                                    )}
                                    <td className="px-4 py-3 whitespace-nowrap text-right text-slate-700">
                                        {parseFloat(row.SoldQty || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-indigo-600">
                                        ₹{totalAmount.toFixed(2)}
                                    </td>
                                    {showBalanceDue && (
                                        <td
                                            className={`px-4 py-3 whitespace-nowrap text-right font-medium ${!isCredit && balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}
                                        >
                                            {isCredit ? '—' : `₹${balanceDue.toFixed(2)}`}
                                        </td>
                                    )}
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <PaymentStatusCell balanceDue={balanceDue} isIpCredit={isCredit} />
                                    </td>
                                    {showActions && (
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => onEdit?.(row)}
                                                    className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                                    title="Edit Bill"
                                                >
                                                    <FiEdit2 size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => onDelete?.(row)}
                                                    className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                                                    title="Delete/Cancel Bill"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                                {isExpanded && (
                                    <tr className="bg-slate-50/80">
                                        <td colSpan={colCount} className="px-4 py-4">
                                            <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                                Invoice Items
                                                {details?.details?.length ? ` (${details.details.length})` : ''}
                                            </h4>
                                            <InvoiceItemsDetailTable
                                                details={details}
                                                invoiceKey={invoiceKey}
                                                loadingDetails={loadingDetails}
                                                userInfo={userInfo}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
